import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { FieldTypes, filterFieldsForForm } from 'drapcode-constant';
import { iconForField } from '../../../../common/custom/icon-library';
import { Mixpanel } from '../../../../common/utils/mixpanelUtil';
import CustomSelect from '../../../home/collections/CustomSelect';
import { addRule, createSelectOptionsForField, removeRule, updateRule } from './workflow-util';
import WorkflowRuleForm from './WorkflowRuleForm';

export default class WorkflowForm extends Component {
  static propTypes = {
    actions: PropTypes.object.isRequired,
    projectId: PropTypes.string.isRequired,
    collection: PropTypes.object.isRequired,
    collections: PropTypes.object.isRequired,
  };

  state = {
    name: '',
    keyField: '',
    rules: [],
  };

  handleWorkflowSubmit = (values) => {
    const { rules } = this.state;
    const { name, keyField } = values;
    const finalObj = { name, keyField, workflowRules: rules };
    this.props.handleSubmit(finalObj);
  };

  clearForm = () => {
    this.setState({
      name: '',
      keyField: '',
      rules: [],
    });
  };

  getRoleOption = () => {
    let options = [];

    const { roles } = this.props;
    const roleOptions =
      roles.length > 0
        ? roles.map((role, key) => {
            return { value: role.name, label: role.name };
          })
        : [];
    return [...options, ...roleOptions];
  };

  handleChange = (name, value) => {
    this.setState({ [name]: value });
  };

  addWorkflowRule = () => {
    const rules = addRule(this.state.rules);
    this.setState({ rules });
  };

  removeWorkflowRule = (id) => {
    const rules = removeRule(this.state.rules, id);
    this.setState({ rules });
  };

  saveWorkflowRule = (id, ruleObj) => {
    const rules = updateRule(this.state.rules, id, ruleObj);
    this.setState({ rules });
  };

  createAddRuleBtn = (setFieldValue, setFieldTouched) => {
    const { isReadOnly } = this.props;
    return this.state.keyField ? (
      <div className="col-md-12 col-lg-12 text-right">
        <button
          type="button"
          className="btn btn-theme-green btn-sm"
          onClick={() => {
            isReadOnly ? null : this.addWorkflowRule();
            Mixpanel.track('Clicked on - Add Rule in collection workflow');
          }}
          disabled={isReadOnly}
          title={isReadOnly && 'Read-Only Mode'}
        >
          <i className="wb webconnect-plus"></i>&nbsp;Add Rule
        </button>
      </div>
    ) : (
      ''
    );
  };

  createFormForRule = (setFieldValue, setFieldTouched) => {
    const { rules, keyField } = this.state;
    const { collection, collectionItems, collections, actions, projectId, roles, isReadOnly } =
      this.props;
    const collectionFields = createSelectOptionsForField(collection.fields);
    if (rules && rules.length > 0) {
      return rules.map((rule) => (
        <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12 drapcode" key={rule.id}>
          <div className="card">
            <div className="card-header card-header-text" data-background-color="drapcode-violet">
              <h6 className="card-title pb-0 mb-0 text-capitalise">Rule #{rule.id}</h6>
            </div>
            <div className="card-content">
              <div className="row mx-auto" key={rule.id}>
                <WorkflowRuleForm
                  keyField={keyField}
                  roles={roles}
                  rule={rule}
                  collections={collections}
                  collectionItems={collectionItems}
                  actions={actions}
                  projectId={projectId}
                  collection={collection}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  fieldOptions={collectionFields}
                  fields={collection.fields}
                  removeRule={this.removeWorkflowRule}
                  saveWorkflowRule={this.saveWorkflowRule}
                  handleChange={this.handleChange}
                  isReadOnly={isReadOnly}
                />
              </div>
            </div>
          </div>
        </div>
      ));
    } else {
      return '';
    }
  };

  createCollectionKeyFieldSelector = (setFieldValue, setFieldTouched) => {
    const { collection } = this.props;
    const collectionFields = createSelectOptionsForField(collection.fields);
    return (
      <div className="form-group">
        <div className="input-group">
          <div
            className="input-group-prepend"
            title="Select Field on which you want to apply Workflow"
          >
            <span className="input-group-text">
              <i className={`wb ${iconForField('select')}`}></i>
              <span className="required"> *</span>
            </span>
          </div>
          <CustomSelect
            name="keyField"
            isMulti={false}
            value={this.state.keyField}
            options={collectionFields}
            onChange={setFieldValue}
            onBlur={setFieldTouched}
            onChangeValue={this.handleChange}
            placeholder="Select Field on which you want to apply Workflow"
          />
          <ErrorMessage name="keyField">
            {(msg) => <p className="text-error">{msg}</p>}
          </ErrorMessage>
        </div>
      </div>
    );
  };

  showWorkflowSaveButton = () => {
    const { rules } = this.state;
    const { isReadOnly } = this.props;
    return rules && Object.keys(rules).length > 0 && rules[0].nextValue.length > 0 ? (
      <button
        type="submit"
        className="btn btn-theme-submit btn-md"
        disabled={isReadOnly}
        title={isReadOnly && 'Read-Only Mode'}
      >
        <i className="wb webconnect-plus"></i>&nbsp;Save
      </button>
    ) : (
      ''
    );
  };

  render() {
    const { collection, isReadOnly } = this.props;
    const fields = filterFieldsForForm(collection.fields);
    if (!fields || fields.length === 0) {
      return <p>Please add some fields in you collection</p>;
    } else {
      const staticOptions = fields.filter((field) => field.type === FieldTypes.static_option.id);
      if (!staticOptions || staticOptions.length === 0) {
        return <p>Please add static option field in your collection</p>;
      }
    }
    return (
      <>
        <Formik
          enableReinitialize={true}
          initialValues={{
            name: '',
            keyField: '',
            rules: [],
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string()
              .required("Name can't be blank")
              .min(3, `Name can\'t be less than 3 characters`)
              .matches(/^[0-9A-Za-z\s]+$/, 'Name should not contain special characters or numbers.')
              .max(100, `Name can\'t be more than 100 characters`),
            keyField: Yup.object()
              .required('Choose the Select Field on which you want to apply Workflow')
              .nullable(),
          })}
          onSubmit={(values, { setSubmitting, resetForm, setErrors }) => {
            isReadOnly ? null : this.handleWorkflowSubmit(values);
            setSubmitting(false);
            this.clearForm();
            resetForm();
          }}
        >
          {({ isSubmitting, values, setFieldValue, setFieldTouched, errors }) => (
            <Form className="form-horizontal">
              <div className="row mx-auto">
                <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12">
                  <div className="form-group">
                    <div className="input-group">
                      <div className="input-group-prepend" title="Workflow Name">
                        <span className="input-group-text">
                          <i className={`wb ${iconForField('text')}`}></i>
                          <span className="required"> *</span>
                        </span>
                      </div>
                      <Field
                        type="text"
                        name="name"
                        className="form-control form-control-sm"
                        placeholder="Name of Workflow"
                      />
                      <ErrorMessage name="name">
                        {(msg) => <p className="text-error">{msg}</p>}
                      </ErrorMessage>
                    </div>
                  </div>
                </div>
                <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12">
                  {this.createCollectionKeyFieldSelector(setFieldValue, setFieldTouched)}
                </div>

                {this.createFormForRule(setFieldValue, setFieldTouched)}
                {this.createAddRuleBtn(setFieldValue, setFieldTouched)}

                <div className="col-md-12 col-lg-12 text-right mt-4">
                  <button type="reset" className="btn btn-default btn-md" onClick={this.clearForm}>
                    <i className="wb webconnect-eraser"></i>&nbsp;Reset
                  </button>
                  &nbsp;
                  {this.showWorkflowSaveButton()}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </>
    );
  }
}
